/**
 * ERP-FIN-02 Gate AR-01: AR Invoice Workspace
 * Route: /ar/invoices  (ar.invoice.view)
 *
 * Lifecycle: DRAFT → APPROVED → ISSUED → (VOID)
 * Supports: manual invoices and proposal milestone-generated drafts.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Search, FileText, CheckCircle2, Clock,
  XCircle, Send, Archive, ChevronRight, Building2, CalendarDays,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arInvoiceApi, formatError } from "@/services/api";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(minor / 100);
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const STATUS_CFG = {
  DRAFT:    { label: "Draft",    bg: "#EFF6FF", text: "#1D4ED8", border: "#BFDBFE", Icon: Clock },
  APPROVED: { label: "Approved", bg: "#FEF3C7", text: "#92400E", border: "#FDE68A", Icon: CheckCircle2 },
  ISSUED:   { label: "Issued",   bg: "#F0FDF4", text: "#166534", border: "#BBF7D0", Icon: Send },
  VOID:     { label: "Void",     bg: "#F5F5F5", text: "#6B7280", border: "#E5E7EB", Icon: Archive },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || { label: status, bg: "#F5F5F5", text: "#666", border: "#DDD", Icon: FileText };
  const Ic = cfg.Icon;
  return (
    <span data-testid={`status-badge-${status?.toLowerCase()}`} style={{
      display: "inline-flex", alignItems: "center", gap: "4px",
      padding: "3px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 700,
      background: cfg.bg, color: cfg.text, border: `1px solid ${cfg.border}`,
    }}>
      <Ic size={10} /> {cfg.label}
    </span>
  );
}

const FILTERS = [
  { label: "All",      value: "" },
  { label: "Draft",    value: "DRAFT" },
  { label: "Approved", value: "APPROVED" },
  { label: "Issued",   value: "ISSUED" },
  { label: "Void",     value: "VOID" },
];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ARInvoiceWorkspace() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read event context from URL params (e.g. navigated from Event Finance workspace)
  const urlEventMasterId = searchParams.get("event_master_id") || "";
  const urlEventName     = searchParams.get("event_name") || "";
  const urlOpenCreate    = searchParams.get("open_create") === "1";

  const [invoices,    setInvoices]    = useState([]);
  const [total,       setTotal]       = useState(0);
  const [counts,      setCounts]      = useState({ DRAFT: 0, APPROVED: 0, ISSUED: 0, VOID: 0 });
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState("");
  const [search,      setSearch]      = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page,        setPage]        = useState(1);

  // Create modal
  const [showCreate,  setShowCreate]  = useState(false);
  const [customers,   setCustomers]   = useState([]);
  const [legalEntities, setLEs]       = useState([]);
  const [creating,    setCreating]    = useState(false);
  const [form, setForm] = useState({
    customer_id: "", legal_entity_id: "",
    invoice_date: new Date().toISOString().slice(0, 10),
    due_date: "",
    accounting_date: new Date().toISOString().slice(0, 10),
    currency_code: "INR", invoice_notes: "", internal_notes: "",
    event_master_id: urlEventMasterId,
    event_name_display: urlEventName,
  });

  // Auto-open create modal if URL param says so
  useEffect(() => {
    if (urlOpenCreate && canCreate) {
      setShowCreate(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const PAGE_SIZE = 25;
  const hasCap = (cap) => user?.capabilities?.includes(cap);
  const canCreate  = hasCap("ar.invoice.create");
  const canView    = hasCap("ar.invoice.view");

  // ── Load ──────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filter)  params.status = filter;
      if (search)  params.search = search;
      const res = await arInvoiceApi.list(params);
      setInvoices(res.data.invoices || []);
      setTotal(res.data.total || 0);
      setCounts(res.data.counts || { DRAFT: 0, APPROVED: 0, ISSUED: 0, VOID: 0 });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [page, filter, search]);

  useEffect(() => { load(); }, [load]);

  const loadCreateDeps = useCallback(async () => {
    if (!showCreate) return;
    try {
      const [custRes, leRes] = await Promise.all([
        axios.get(`${API}/api/customers`, { withCredentials: true }),
        axios.get(`${API}/api/organization/legal-entities`, { withCredentials: true }),
      ]);
      setCustomers(Array.isArray(custRes.data) ? custRes.data : (custRes.data?.items || []));
      setLEs(Array.isArray(leRes.data) ? leRes.data : (leRes.data?.items || []));
    } catch { /* non-blocking */ }
  }, [showCreate]);

  useEffect(() => { loadCreateDeps(); }, [loadCreateDeps]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSearch = () => { setSearch(searchInput); setPage(1); };
  const handleSearchKey = (e) => { if (e.key === "Enter") handleSearch(); };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.customer_id || !form.legal_entity_id || !form.due_date) {
      toast.error("Customer, legal entity and due date are required"); return;
    }
    setCreating(true);
    try {
      const payload = {
        customer_id:      form.customer_id,
        legal_entity_id:  form.legal_entity_id,
        invoice_date:     form.invoice_date,
        due_date:         form.due_date,
        accounting_date:  form.accounting_date,
        currency_code:    form.currency_code,
        invoice_notes:    form.invoice_notes,
        internal_notes:   form.internal_notes,
      };
      // Only include event_master_id when set (preserves standalone invoices)
      if (form.event_master_id) payload.event_master_id = form.event_master_id;

      const res = await arInvoiceApi.create(payload);
      toast.success(`Invoice ${res.data.invoice_number} created (DRAFT)`);
      setShowCreate(false);
      setForm({
        customer_id: "", legal_entity_id: "",
        invoice_date: new Date().toISOString().slice(0, 10),
        due_date: "", accounting_date: new Date().toISOString().slice(0, 10),
        currency_code: "INR", invoice_notes: "", internal_notes: "",
        event_master_id: "", event_name_display: "",
      });
      navigate(`/ar/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCreating(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div data-testid="ar-invoice-workspace" style={{ padding: "24px", maxWidth: "1280px", margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <h1 style={{ fontSize: "22px", fontWeight: 700, color: "#111827", margin: 0 }}>
            Customer Invoices
          </h1>
          <p style={{ color: "#6B7280", fontSize: "13px", marginTop: "4px" }}>
            ERP-FIN-02 · Gate AR-01 · Customer Invoice Foundation
          </p>
        </div>
        {canCreate && (
          <button
            data-testid="create-invoice-btn"
            onClick={() => setShowCreate(true)}
            style={{
              display: "flex", alignItems: "center", gap: "6px",
              background: "#0176D3", color: "#fff", border: "none",
              padding: "8px 16px", borderRadius: "6px", cursor: "pointer",
              fontSize: "13px", fontWeight: 600,
            }}
          >
            <Plus size={15} /> New Invoice
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: "12px", marginBottom: "20px" }}>
        {[
          { key: "DRAFT",    label: "Draft",    color: "#1D4ED8", bg: "#EFF6FF" },
          { key: "APPROVED", label: "Approved", color: "#92400E", bg: "#FEF3C7" },
          { key: "ISSUED",   label: "Issued",   color: "#166534", bg: "#F0FDF4" },
          { key: "VOID",     label: "Void",     color: "#6B7280", bg: "#F5F5F5" },
        ].map(({ key, label, color, bg }) => (
          <div
            key={key}
            data-testid={`count-card-${key.toLowerCase()}`}
            onClick={() => { setFilter(key); setPage(1); }}
            style={{
              background: filter === key ? bg : "#fff",
              border: `1px solid ${filter === key ? color + "50" : "#E5E7EB"}`,
              borderRadius: "8px", padding: "16px", cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: 700, color }}>{counts[key] ?? 0}</div>
            <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Filter tabs + Search */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: "4px", background: "#F3F4F6", borderRadius: "8px", padding: "4px" }}>
          {FILTERS.map(f => (
            <button
              key={f.value}
              data-testid={`filter-tab-${f.value || "all"}`}
              onClick={() => { setFilter(f.value); setPage(1); }}
              style={{
                padding: "5px 14px", borderRadius: "6px", border: "none",
                background: filter === f.value ? "#fff" : "transparent",
                color: filter === f.value ? "#111827" : "#6B7280",
                fontWeight: filter === f.value ? 600 : 400,
                fontSize: "13px", cursor: "pointer",
                boxShadow: filter === f.value ? "0 1px 3px rgba(0,0,0,.1)" : "none",
              }}
            >{f.label}</button>
          ))}
        </div>

        <div style={{ display: "flex", gap: "8px", marginLeft: "auto" }}>
          <div style={{ display: "flex", background: "#fff", border: "1px solid #E5E7EB", borderRadius: "6px", overflow: "hidden" }}>
            <input
              data-testid="invoice-search-input"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKey}
              placeholder="Search invoice # or customer…"
              style={{ border: "none", outline: "none", padding: "7px 12px", fontSize: "13px", width: "220px" }}
            />
            <button onClick={handleSearch} style={{ background: "none", border: "none", cursor: "pointer", padding: "0 10px", color: "#6B7280" }}>
              <Search size={14} />
            </button>
          </div>
          <button
            data-testid="refresh-btn"
            onClick={load}
            style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "6px", padding: "7px 10px", cursor: "pointer", color: "#6B7280" }}
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      {/* Invoice List */}
      <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: "10px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "48px", textAlign: "center", color: "#6B7280" }}>Loading invoices…</div>
        ) : invoices.length === 0 ? (
          <div style={{ padding: "48px", textAlign: "center", color: "#9CA3AF" }}>
            <FileText size={36} style={{ marginBottom: "12px", opacity: 0.4 }} />
            <div style={{ fontWeight: 600, marginBottom: "4px" }}>No invoices found</div>
            <div style={{ fontSize: "13px" }}>
              {canCreate ? "Create a new invoice to get started." : "No customer invoices match the current filters."}
            </div>
          </div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                  {["Invoice #", "Customer", "Invoice Date", "Due Date", "Period", "Total", "Status", ""].map(h => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontSize: "11px", fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv, idx) => (
                  <tr
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    onClick={() => navigate(`/ar/invoices/${inv.id}`)}
                    style={{
                      borderBottom: idx < invoices.length - 1 ? "1px solid #F3F4F6" : "none",
                      cursor: "pointer", transition: "background 0.1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = "#F9FAFB"}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <td style={{ padding: "12px 16px" }}>
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#0176D3" }}>{inv.invoice_number || "—"}</span>
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <div style={{ fontSize: "13px", fontWeight: 500, color: "#111827" }}>{inv.customer_name || "—"}</div>
                      {inv.event_name && <div style={{ fontSize: "11px", color: "#6B7280" }}>{inv.event_name}</div>}
                    </td>
                    <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151" }}>{fmtDate(inv.invoice_date)}</td>
                    <td style={{ padding: "12px 16px", fontSize: "13px", color: "#374151" }}>{fmtDate(inv.due_date)}</td>
                    <td style={{ padding: "12px 16px", fontSize: "12px", color: "#6B7280" }}>{inv.period_name || "—"}</td>
                    <td style={{ padding: "12px 16px", fontSize: "13px", fontWeight: 600, color: "#111827", textAlign: "right" }}>
                      {fmt(inv.total_minor, inv.currency_code)}
                    </td>
                    <td style={{ padding: "12px 16px" }}><StatusBadge status={inv.status} /></td>
                    <td style={{ padding: "12px 16px", color: "#9CA3AF" }}><ChevronRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #F3F4F6", fontSize: "13px" }}>
                <span style={{ color: "#6B7280" }}>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button data-testid="prev-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}
                    style={{ padding: "4px 12px", border: "1px solid #E5E7EB", borderRadius: "6px", cursor: page === 1 ? "not-allowed" : "pointer", background: "#fff", opacity: page === 1 ? 0.5 : 1 }}>
                    Previous
                  </button>
                  <button data-testid="next-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                    style={{ padding: "4px 12px", border: "1px solid #E5E7EB", borderRadius: "6px", cursor: page >= totalPages ? "not-allowed" : "pointer", background: "#fff", opacity: page >= totalPages ? 0.5 : 1 }}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Invoice Modal */}
      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
          <div data-testid="create-invoice-modal" style={{ background: "#fff", borderRadius: "12px", padding: "28px", width: "100%", maxWidth: "560px", maxHeight: "90vh", overflowY: "auto" }}>
            <h2 style={{ fontSize: "18px", fontWeight: 700, marginBottom: "20px", color: "#111827" }}>Create Customer Invoice</h2>
            <form onSubmit={handleCreate}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>

                {/* Event Attribution — shows prominently when pre-populated from event context */}
                {form.event_master_id ? (
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Event Attribution</label>
                    <div data-testid="event-attribution-badge" style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 12px", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: "8px" }}>
                      <CalendarDays size={14} color="#1D4ED8" />
                      <span style={{ fontSize: "13px", fontWeight: 600, color: "#1D4ED8" }}>{form.event_name_display || form.event_master_id}</span>
                      <button
                        type="button"
                        data-testid="clear-event-attribution-btn"
                        onClick={() => setForm(f => ({ ...f, event_master_id: "", event_name_display: "" }))}
                        style={{ marginLeft: "auto", fontSize: "11px", color: "#6B7280", background: "none", border: "none", cursor: "pointer", padding: "2px 6px" }}
                      >
                        Clear
                      </button>
                    </div>
                    <p style={{ fontSize: "11px", color: "#6B7280", marginTop: "3px" }}>This invoice will be attributed to the event above. Clear to create a standalone invoice.</p>
                  </div>
                ) : (
                  <div style={{ gridColumn: "1/-1" }}>
                    <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Event Attribution (Optional)</label>
                    <input
                      data-testid="event-master-id-input"
                      value={form.event_master_id}
                      onChange={e => setForm(f => ({ ...f, event_master_id: e.target.value }))}
                      placeholder="Paste Event Master ID to attribute this invoice to an event…"
                      style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }}
                    />
                    <p style={{ fontSize: "11px", color: "#6B7280", marginTop: "3px" }}>Leave blank for a standalone invoice not tied to any event.</p>
                  </div>
                )}
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Customer *</label>
                  <select
                    data-testid="customer-select"
                    required
                    value={form.customer_id}
                    onChange={e => setForm(f => ({ ...f, customer_id: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" }}
                  >
                    <option value="">— Select Customer —</option>
                    {customers.map(c => <option key={c._id || c.id} value={c._id || c.id}>{c.name || c.customer_name}</option>)}
                  </select>
                </div>

                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Legal Entity *</label>
                  <select
                    data-testid="legal-entity-select"
                    required
                    value={form.legal_entity_id}
                    onChange={e => setForm(f => ({ ...f, legal_entity_id: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" }}
                  >
                    <option value="">— Select Legal Entity —</option>
                    {legalEntities.map(le => <option key={le._id || le.id} value={le._id || le.id}>{le.name}</option>)}
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Invoice Date *</label>
                  <input data-testid="invoice-date-input" type="date" required value={form.invoice_date}
                    onChange={e => setForm(f => ({ ...f, invoice_date: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }} />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Due Date *</label>
                  <input data-testid="due-date-input" type="date" required value={form.due_date}
                    onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }} />
                </div>

                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Accounting Date *</label>
                  <input data-testid="accounting-date-input" type="date" required value={form.accounting_date}
                    onChange={e => setForm(f => ({ ...f, accounting_date: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px", boxSizing: "border-box" }} />
                  <p style={{ fontSize: "11px", color: "#6B7280", marginTop: "3px" }}>Must fall within an OPEN fiscal period for the selected legal entity.</p>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Currency</label>
                  <select data-testid="currency-select" value={form.currency_code}
                    onChange={e => setForm(f => ({ ...f, currency_code: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px" }}>
                    <option value="INR">INR — Indian Rupee</option>
                    <option value="USD">USD — US Dollar</option>
                    <option value="EUR">EUR — Euro</option>
                  </select>
                </div>

                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#374151", marginBottom: "4px" }}>Invoice Notes (Customer-facing)</label>
                  <textarea data-testid="invoice-notes-input" value={form.invoice_notes}
                    onChange={e => setForm(f => ({ ...f, invoice_notes: e.target.value }))}
                    rows={2} placeholder="Optional notes visible on the invoice PDF…"
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #D1D5DB", borderRadius: "6px", fontSize: "13px", resize: "vertical", boxSizing: "border-box" }} />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button type="button" onClick={() => setShowCreate(false)}
                  style={{ padding: "8px 18px", border: "1px solid #D1D5DB", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "13px" }}>
                  Cancel
                </button>
                <button type="submit" data-testid="create-invoice-submit" disabled={creating}
                  style={{ padding: "8px 18px", background: creating ? "#93C5FD" : "#0176D3", color: "#fff", border: "none", borderRadius: "6px", cursor: creating ? "not-allowed" : "pointer", fontSize: "13px", fontWeight: 600 }}>
                  {creating ? "Creating…" : "Create Invoice"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
